export const LINKFOX_VIDEO_PRICING_SOURCE = Object.freeze({
  capturedAt: '2026-06-25',
  pageUrl: 'https://www.linkfox.com/draw/video',
  configChunk: 'https://www.linkfox.com/assets/config-CdFOQwXp.js',
  calculatorChunk: 'https://www.linkfox.com/assets/Image2Video-UzjwvsHB.js',
  calculatorRule: 'promoCostPoints[type][time][tier] ?? frameCostPoints[type][time][tier] ?? costPoints[type][time][tier]',
});

const TYPE_ALIASES = Object.freeze({
  MODEL: 'KLING',
  PRODUCT: 'HAILUO',
  MOVIE: 'V',
  HIGH_QUALITY: 'V',
});

const RESOLUTION_COST_MAP = Object.freeze({
  KLING: Object.freeze({
    '720p': 'standard',
    '1080p': 'pro',
  }),
  MODEL_OMNI: Object.freeze({
    '720p': 'standard',
    '1080p': 'pro',
  }),
  HAILUO: Object.freeze({
    '768p': 'standard',
    '1080p': 'pro',
  }),
  V: Object.freeze({
    '720p': 'standard',
    '1080p': 'pro',
  }),
  SEED_MINI: Object.freeze({
    '480p': 'standard',
    '720p': 'pro',
  }),
  SEED: Object.freeze({
    '480p': 'standard',
    '720p': 'pro',
    '1080p': 'ultra',
  }),
  HAPPY_HORSE: Object.freeze({
    '720p': 'standard',
    '1080p': 'pro',
  }),
  SEED_FAST: Object.freeze({
    '480p': 'standard',
    '720p': 'pro',
  }),
});

const COST_POINTS = Object.freeze({
  KLING: Object.freeze({
    5: Object.freeze({ standard: 300, pro: 400 }),
    10: Object.freeze({ standard: 600, pro: 800 }),
    15: Object.freeze({ standard: 900, pro: 1200 }),
  }),
  MODEL_OMNI: Object.freeze({
    5: Object.freeze({ standard: 300, pro: 400 }),
    10: Object.freeze({ standard: 600, pro: 800 }),
    15: Object.freeze({ standard: 900, pro: 1200 }),
  }),
  HAILUO: Object.freeze({
    6: Object.freeze({ standard: 200, pro: 300 }),
    10: Object.freeze({ standard: 400, pro: 500 }),
  }),
  WAN: Object.freeze({
    5: Object.freeze({ standard: 300 }),
    10: Object.freeze({ standard: 600 }),
    15: Object.freeze({ standard: 800 }),
  }),
  V: Object.freeze({
    8: Object.freeze({ standard: 700, pro: 800 }),
  }),
  SEED: Object.freeze({
    5: Object.freeze({ standard: 200, pro: 400, ultra: 1500 }),
    10: Object.freeze({ standard: 400, pro: 800, ultra: 3000 }),
    15: Object.freeze({ standard: 600, pro: 1200, ultra: 4500 }),
  }),
  SEED_MINI: Object.freeze({
    5: Object.freeze({ standard: 175, pro: 325 }),
    10: Object.freeze({ standard: 350, pro: 650 }),
    15: Object.freeze({ standard: 525, pro: 975 }),
  }),
  HAPPY_HORSE: Object.freeze({
    5: Object.freeze({ standard: 300, pro: 500 }),
    10: Object.freeze({ standard: 600, pro: 1000 }),
    15: Object.freeze({ standard: 900, pro: 1500 }),
  }),
  SEED_FAST: Object.freeze({
    5: Object.freeze({ standard: 175, pro: 325 }),
    10: Object.freeze({ standard: 350, pro: 650 }),
    15: Object.freeze({ standard: 525, pro: 975 }),
  }),
});

const FRAME_COST_POINTS = Object.freeze({
  KLING: Object.freeze({
    5: Object.freeze({ standard: 200, pro: 300, ultra: 400 }),
    10: Object.freeze({ standard: 400, pro: 500, ultra: 800 }),
  }),
});

const PROMO_COST_POINTS = Object.freeze({
  SEED_FAST: Object.freeze({
    5: Object.freeze({ standard: 125, pro: 225 }),
    10: Object.freeze({ standard: 250, pro: 450 }),
    15: Object.freeze({ standard: 375, pro: 675 }),
  }),
});

export const LINKFOX_VIDEO_PRICING = Object.freeze({
  source: LINKFOX_VIDEO_PRICING_SOURCE,
  resolutionCostMap: RESOLUTION_COST_MAP,
  costPoints: COST_POINTS,
  frameCostPoints: FRAME_COST_POINTS,
  promoCostPoints: PROMO_COST_POINTS,
});

export function normalizeLinkfoxVideoType(type) {
  const key = String(type || '').trim().toUpperCase();
  const normalized = TYPE_ALIASES[key] || key;
  return COST_POINTS[normalized] ? normalized : 'SEED_FAST';
}

export function normalizeLinkfoxResolution(resolution = '') {
  const text = String(resolution || '').trim().toLowerCase();
  if (!text) return '';
  return text.endsWith('p') ? text : `${text}p`;
}

function normalizeDuration(videoTime, fallback = 10) {
  const value = Number(videoTime);
  if (!Number.isFinite(value) || value <= 0) return fallback;
  return Math.trunc(value);
}

function resolveTier({ videoType, resolution = '', isPro = false } = {}) {
  const type = normalizeLinkfoxVideoType(videoType);
  const normalizedResolution = normalizeLinkfoxResolution(resolution);
  const fromResolution = RESOLUTION_COST_MAP[type]?.[normalizedResolution];
  if (fromResolution) return fromResolution;
  return isPro ? 'pro' : 'standard';
}

function readCost(table, duration, tier) {
  const row = table?.[duration];
  if (!row) return null;
  return row[tier] ?? row.standard ?? null;
}

export function estimateLinkfoxVideoCredits({
  type,
  videoType = type,
  time,
  videoTime = time,
  resolution = '',
  mode,
  inputMode = mode,
  voice = false,
  isPro = false,
} = {}) {
  const normalizedType = normalizeLinkfoxVideoType(videoType);
  const duration = normalizeDuration(videoTime);
  const modeKey = String(inputMode || '').trim();
  const isFrameMode = modeKey === 'firstLast' || modeKey === 'frame';
  const baseTier = resolveTier({ videoType: normalizedType, resolution, isPro });

  if (isFrameMode && FRAME_COST_POINTS[normalizedType]) {
    const frameTier = baseTier === 'pro' && voice ? 'ultra' : baseTier;
    const frameCost = readCost(FRAME_COST_POINTS[normalizedType], duration, frameTier);
    if (frameCost != null) return frameCost;
  }

  const promoCost = readCost(PROMO_COST_POINTS[normalizedType], duration, baseTier);
  if (promoCost != null) return promoCost;

  const regularCost = readCost(COST_POINTS[normalizedType], duration, baseTier);
  if (regularCost != null) return regularCost;

  return 200;
}
